import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import moment from 'moment';

import { IRequestParams, getProductData, getProductsListData } from '@lib/core/products/utils';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { selectBestMatchCharacterForServiceProductCategory } from '@lib/core/users/selectors/characters';
import { selectFidelityCardId } from '@lib/core/users/selectors/fidelityCard';
import { selectWishlistProductInstanceIds } from '@lib/core/users/selectors/productList';
import { isProductInstanceInWishlistFilter } from '@lib/core/users/utils/filters';
import { IProductTip, IUserProductTip } from '@lib/tools/productTip/types/interfaces';

const initialState: IProductTip = {
  currentProductTip: {
    currentProductTipData: null,
    error: null,
    isCurrentProductTipLoading: false,
  },
  usersProductTipsStorage: {},
};

const createProductTipForStorage = (fidelityCardId = '', characterId = '', productTipId = ''): IUserProductTip => ({
  [fidelityCardId]: {
    lastProductTipDate: moment().format('DD.MM.YYYY'),
    productTipCharacter: characterId,
    productTipId,
  },
});

export const fetchAndCreateNewProductTip = createTypedAsyncThunk(
  'productTip/fetchAndCreateNewProductTip',
  async (requestParams: IRequestParams, { getState }) => {
    const state = getState();
    const wishlistProductInstanceIds = selectWishlistProductInstanceIds(state);
    const fidelityCardId = selectFidelityCardId(state);
    const characterId = selectBestMatchCharacterForServiceProductCategory(state)?.characterId;

    const { results: productsList = [] } = await getProductsListData(requestParams);

    const productTipData = productsList.find(
      product => !isProductInstanceInWishlistFilter(wishlistProductInstanceIds, product),
    );

    return {
      productTipData,
      productTipForStorage: createProductTipForStorage(fidelityCardId, characterId, productTipData?.identifier),
    };
  },
);

export const fetchProductTipData = createTypedAsyncThunk(
  'productTip/fetchProductTipData',
  async (productId: string, { getState }) => {
    const state = getState();
    const fidelityCardId = selectFidelityCardId(state);
    const characterId = selectBestMatchCharacterForServiceProductCategory(state)?.characterId;
    const productTipData = await getProductData({ productId });

    return {
      productTipData,
      productTipForStorage: createProductTipForStorage(fidelityCardId, characterId, productId),
    };
  },
);

const productTipSlice = createSlice({
  extraReducers: builder => {
    builder
      .addMatcher(isAnyOf(fetchAndCreateNewProductTip.pending, fetchProductTipData.pending), state => {
        state.currentProductTip = { ...state.currentProductTip, isCurrentProductTipLoading: true };
      })
      .addMatcher(isAnyOf(fetchAndCreateNewProductTip.fulfilled, fetchProductTipData.fulfilled), (state, action) => {
        const { productTipData, productTipForStorage } = action.payload;

        state.currentProductTip = {
          ...state.currentProductTip,
          currentProductTipData: productTipData,
          isCurrentProductTipLoading: false,
        };

        state.usersProductTipsStorage = {
          ...state.usersProductTipsStorage,
          ...productTipForStorage,
        };
      })
      .addMatcher(isAnyOf(fetchAndCreateNewProductTip.rejected, fetchProductTipData.rejected), (state, action) => {
        state.currentProductTip = {
          ...initialState.currentProductTip,
          error: action.error,
          isCurrentProductTipLoading: false,
        };
      });
  },
  initialState,
  name: 'productTip',
  reducers: {
    resetCurrentProductTip(state) {
      state.currentProductTip = initialState.currentProductTip;
    },
  },
});

export const { resetCurrentProductTip } = productTipSlice.actions;
export default productTipSlice.reducer;
